import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, throwError, of, zip } from 'rxjs';
import { environment } from 'src/environments/environment';
import { map, first, share, shareReplay, tap } from 'rxjs/operators';
import { UsuarioCadastro, UserListElement, UsuarioAdminCadastro } from '../models/api-user';
import { DroppedFile, CertificateDroppedFile } from '../models/dropped-file';
import AppResponseError from '../models/app-response-error';
import { EmpresaContratante } from '../models/dados-contratante';
import LoginAuditor, { RetornoLoginAuditor, StatusLoginAuditor } from '../models/login-auditor';
import SelectModel, { GroupSelectModel, Cnae, SelectableModel } from '../models/select-model';
import { TokenLogin, TokenUsuario } from '../models/login-response';
import {
  Cliente, Responsavel, Contato, ResponsavelCadastro, ClienteJustificativaEmLote,
  ClienteCumpriuCarenciaEmLicenca, ClientesSelecaoAtivarInativarCadastroEmLote, ResultadoAtivarInativarClientesEmLote,
  ObrigacoesHistoricoEmpresaCliente
} from '../models/api-clients';
import { OpcoesNotificacaoContatoCliente } from '../models/opcoes-notificacao-contato-cliente';
import { ParametrosAutomacao } from '../models/parametros-automacao';
import { ExcecaoObrigacaoPrazo, ObrigacaoPrazo, Prazos } from '../models/prazos';
import { DadosAuditor } from '../models/dados-auditor';
import { ContratoCadastro, Contrato } from '../models/contrato-cadastro';
import { CertificadoDigitalCollection } from '../models/certificado-digital';
import { LogRequest } from '../models/log-request';
import { RecursoAssinatura } from '../models/recurso-assinatura';
import { QuantidadeDeLicencas, QuantidadeDeLicencasAuditor, mapQuantidadeDeLicencasAuditor } from '../models/licenca';
import { MensagemCaixaDeEntrada, Obrigacao, MensagemCaixaDeSaida, ResponsavelNotificacao } from '../models/central-de-notificacao';
import { Principal } from '../models/dashboard/principal';
import { ProcessosAguardandoAcao } from '../models/dashboard/processo';
import { Automacao, HistoricoEtapa, AcaoUsuarioProcessamento, ExecucaoEtapa } from '../models/dashboard/automacao';
import { RespostaDeProgramasAbertos } from '../models/programas-abertos';
import {
  FiltroCaixaDeEntrada,
  FiltroCaixaDeSaida, FiltroContrato,
  FiltroLicenca,
  FiltroDashboard,
  FiltroCategorias,
  FiltroProcessoParalisado,
  FiltroCertificadoDigital,
  FiltroRelatorio,
  FiltroJustificativaEmLote,
  FiltroContratante
} from '../models/filtros';
import { Graficos } from '../models/dashboard/graficos';
import { Paginador, PaginadorSelecao } from '../models/paginador';
import Procuracao, { ProcuracaoComAdvertencia, ProcuracaoEmProcessamento, AdicionarProcuracaoManualmente } from '../models/procuracao';
import { RefreshRequest } from 'src/app/core/services/auth.service';
import { PrazoCertificadosProcuracoes } from '../models/dashboard/prazo-certificados-procuracoes';
import UsuarioAtivo from '../models/usuario-ativo';
import { DadosRelatorioArquivosTransmitidos, DadosRelatorioConsolidado, DadosRelatorioAnalitico } from '../models/relatorios';
import { DadosRelatorioUtilizacaoProduto, FiltroRelatorioUtilizacaoProduto } from '../models/reports/utilizacao-produto';
import { CardEvolucaoProcessamento, FiltroRelatorioEvolucaoProcessamento } from '../models/reports/evolucao-processamentos';
import { DadosRelatorioLicencas, FiltroRelatorioLicencas } from '../models/reports/license';
import { FiltroDownloadLote } from '../models/dashboard/arquivos-download-lote';
import { JustificativaEmLote } from '../models/dashboard/justificativa-em-lote';
import { EdicaoInicioDasAutomacoes } from '../models/admin/edicaoInicioDasAutomacoes';
import { FiltroRelatorioGestao } from '../models/reports/relatorio-gestao';
import { DownloadObject } from '../models/reports/download-object';
import { MFANotificacao, MFAValidacao } from '../models/multifator';
import { RecuperacaoSenha } from '../models/admin/recuperacaoSenha';
import { IncluirObrigacao } from '../models/bulk-updates/incluir-obrigacao';
import { RemoverObrigacao } from '../models/bulk-updates/remover-obrigacao';
import { IncluirRemoverResponsaveis } from '../models/bulk-updates/incluir-remover-responsaveis';
import { ParametroAutomacaoValues } from '../models/bulk-updates/parametro-automacao';
import { PrazoPorEtapa } from '../models/bulk-updates/prazo-por-etapa';
import { Vigencia } from '../models/datetime';
import { GestaoDeRiscos } from '../models/dashboard/gestao-de-riscos';

const ApiEndpointBase = `${environment.serverAddress}/api`;

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

interface QueryStringParam {
  fieldName: string;
  fieldValue: any;
}


export type QueryStringParams = QueryStringParam[];

interface CachedRequests {
  empresaContratante?: Observable<EmpresaContratante>;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
    private cachedRequests$: CachedRequests = {};

  constructor(private http: HttpClient) { }

  // #region Central de Notificações

  /**
   * Retorna as mensagens da caixa de entrada de acordo com os dados fornecidos no filtro
   */
  buscarMensagensCaixaDeEntrada(filtros: FiltroCaixaDeEntrada): Observable<Paginador<MensagemCaixaDeEntrada>> {

    const httpParams = this.converterFiltroParaQueryParam(filtros);

    return this.http
      .get<Paginador<MensagemCaixaDeEntrada>>(
        `${ApiEndpointBase}/central-de-notificacoes/entrada`,
        { params: httpParams }
      );
  }

  buscarUsuariosMensagensCaixaDeEntrada(idMensagem: number): Observable<ResponsavelNotificacao[]>
  {
    return this.http
      .get<ResponsavelNotificacao[]>(`${ApiEndpointBase}/central-de-notificacoes/entrada/${idMensagem}/usuario-notificacao`);
  }

  /**
   * Retorna o conteúdo de uma mensagem e marca a mesma como lida
   * @param mensagemId Id da mensagem.
   */
  buscarMensagemCaixaDeEntrada(mensagemId: number): Observable<MensagemCaixaDeEntrada> {
    return this.http.get<MensagemCaixaDeEntrada>(`${ApiEndpointBase}/central-de-notificacoes/entrada/${mensagemId}`);
  }

  /**
   * Retorna as mensagens da caixa de saida de acordo com os dados fornecidos no filtro
   */
  buscarMensagensCaixaDeSaida(filtros: FiltroCaixaDeSaida): Observable<Paginador<MensagemCaixaDeSaida>> {
    const httpParams = this.converterFiltroParaQueryParam(filtros);

    return this.http
      .get<Paginador<MensagemCaixaDeSaida>>(
        `${ApiEndpointBase}/central-de-notificacoes/saida`,
        { params: httpParams }
      );
  }

  // #endregion

  // #region Regras Gerais

  /**
   * Loga informações para a API.
   * Dados são enviados de maneira criptografada. (Com excessão do Conteudo que pode ou não ser enviado criptografado)
   * Utilize a classe GestaoSPED.Util.Criptografia para criptografar.
   * @param log Objeto a ser logado
   */
  loggarErro(log: LogRequest): Observable<any> {
    return this.http.post<LogRequest>(`${ApiEndpointBase}/dominios/log/webapp`, log);
  }

  dataMesPassado(): Observable<Date> {
    return this.http.get<Date>(`${ApiEndpointBase}/dominios/dataMesAnterior`);
  }

  // #endregion

  // #region Login
  definicaoSenha(password: string, authCode: string, primeiroAcesso: boolean): Observable<TokenLogin> {
    return this.http.post<TokenLogin>(`${ApiEndpointBase}/conta/definicao-senha?primeiroAcesso=${primeiroAcesso}`, {
      CodigoAutorizacao: authCode,
      Senha: password
    });
  }

  ativarUsuarioManualmente(email: string, password: string): Observable<any> {
    return this.http.put<any>(`${ApiEndpointBase}/conta/ativar-usuario`, {
      Email: email,
      Senha: password
    });
  }

  obterLinkRecuperacaoSenha(email: string): Observable<RecuperacaoSenha>{
    return this.http.post<RecuperacaoSenha>(`${ApiEndpointBase}/conta/link-recuperacao-senha?email=` + encodeURIComponent(email), undefined);
  }

  verificaCodigoAutorizacao(authCode: string, primeiroAcesso: boolean): Observable<any> {
    return this.http.get(`${ApiEndpointBase}/conta/verifica-codigo?codigoAutorizacao=${authCode}&primeiroAcesso=${primeiroAcesso}`);
  }

  /**
   * Dado o accessToken salvo no localStorage, verifica sua integridade e retorna dados
   * @param accessToken Token de acesso para ser validado
   */
  validarToken(): Observable<TokenUsuario> {
    return this.http.get<TokenUsuario>(`${ApiEndpointBase}/conta/usuario`);
  }
  // #endregion

  // #region Admin do produto

  /**
   * Função parecida com a login normal, porém só aceita para perfil de Admin do Prod.
   * @param user email
   * @param passwd senha
   */
  doAdminLogin(user: string, passwd: string): Observable<TokenLogin> {
    return this.http.post<TokenLogin>(`${ApiEndpointBase}/conta/login-admin`, {
      Email: user,
      Senha: passwd
    });
  }

  /**
   * Cria um novo admin do sistema (maior que admin da Empr. Contratante)
   * @param user Novo usuário a ser criado.
   */
  createNewAdmin(user: UsuarioAdminCadastro): Observable<any> {
    return this.http.post<any>(`${ApiEndpointBase}/admin-produto`, user);
  }

  /**
   * Retorna uma lista com todas as empresas contratantes pro admin ver.
   * @param order Parametro usado para ordenar a lista das contratantes.
   */
  obterTodasContratantes(order?: string): Observable<EmpresaContratante[]> {
    return this.http.get<EmpresaContratante[]>(`${ApiEndpointBase}/contratante?order=${order}`);
  }

  /**
   * Retorna todas as contratantes completas, filtrando apenas pelo produto selecionado (id único).
   * @param produtoId Id do produto (ex: 1)
   */
  obterTodasContratantesCompletas(produtoId?: number): Observable<EmpresaContratante[]> {
    let params = new HttpParams();

    if (produtoId && produtoId > 0) {
      params = params.set('produto', produtoId.toString());
    }

    return this.http.get<EmpresaContratante[]>(`${ApiEndpointBase}/contratante/completo`, { params });
  }

  /**
   * Retorna uma lista com todas as empresas contratantes pro admin ver.
   * @param order Parametro usado para ordenar a lista das contratantes.
   */
    obterTodasContratantesPaginadas(filtro: FiltroContratante): Observable<Paginador<EmpresaContratante>> {
      const httpParams = this.converterFiltroParaQueryParam(filtro);
      return this.http.get<Paginador<EmpresaContratante>>(`${ApiEndpointBase}/contratante/paginado`, { params: httpParams });
    }

  /**
   * Retorna uma lista com todos os admins até o momento.
   */
  obterAdminsDoProduto(): Observable<UsuarioAdminCadastro[]> {
    return this.http.get<UsuarioAdminCadastro[]>(`${ApiEndpointBase}/admin-produto`);
  }

  /**
   * Retorna os prazos na visão do admin
   */
  obterPrazosIcmsIpiAdmin(): Observable<Prazos[]> {
    return this.http.get<Prazos[]>(`${ApiEndpointBase}/admin-produto/prazo-icms-ipi`);
  }

  criarObrigacaoPrazoIcmsIpiAdmin(prazo: Prazos) {
    return this.http.post<Prazos>(`${ApiEndpointBase}/admin-produto/prazo-icms-ipi`, prazo);
  }

  atualizarObrigacaoPrazoIcmsIpiAdmin(prazo: Prazos) {
    return this.http.put<Prazos>(`${ApiEndpointBase}/admin-produto/prazo-icms-ipi`, prazo);
  }

  deletaObrigacaoPrazoIcmsIpiAdmin(prazoId: number): Observable<any> {
    return this.http.delete(`${ApiEndpointBase}/admin-produto/prazo-icms-ipi/${prazoId}`);
  }

  obterUsuariosAtivos(
  empresa: EmpresaContratante,
  nomeOuEmail: string,
  canalID: number,
  produtoId?: number
  ): Observable<UsuarioAtivo[]> {
  let params = new HttpParams()
    .set('EmpresaContratanteId', empresa?.id ?? '')
    .set('NomeOuEmailUsuario', nomeOuEmail ?? '')
    .set('CanalDeVendaID', canalID ?? '');

  if (produtoId && produtoId > 0) {
    params = params.set('ProdutosIDs', produtoId.toString());
  }

    return this.http.get<UsuarioAtivo[]>(`${ApiEndpointBase}/usuario/ativo`, { params });
  }

  obterUsuariosAtivosPorPagina(
    pageIndex: number,
    empresa: EmpresaContratante,
    nomeOuEmail: string,
    canalID: number,
    produtoId?: number
  ) {
    let params = new HttpParams()
      .set('NumeroPagina', pageIndex)
      .set('EmpresaContratanteId', empresa?.id ?? '')
      .set('NomeOuEmailUsuario', nomeOuEmail ?? '')
      .set('CanalDeVendaID', canalID ?? '');

    if (produtoId && produtoId > 0) {
      params = params.set('ProdutosIDs', produtoId.toString());
    }

    return this.http.get<Paginador<UsuarioAtivo>>(`${ApiEndpointBase}/usuario/ativo/paginado`, { params });
  }

  obterUsuariosAtivosFiltro() {
    return this.http.get<UserListElement[]>(`${ApiEndpointBase}/usuario/ativos-contratante`);
  }

  // #endregion

  // #region Contrato
  salvarContrato(contrato: ContratoCadastro): Observable<number> {
    return this.http.post<number>(`${ApiEndpointBase}/contrato`, contrato);
  }

  /**
   * Dada as opções de filtro, retorna um conjunto de contratos
   * @param filtros filtros
   */
  obterContratos(filtros: FiltroContrato): Observable<Contrato[]> {
    return this.http
      .get<Contrato[]>(`${ApiEndpointBase}/contrato`,
        { params: filtros as HttpParams }
      );
  }

  obterContratoPorContratante(): Observable<Contrato> {
    return this.http
      .get<Contrato>(`${ApiEndpointBase}/contrato/contratante`);
  }

  /**
   * Atualiza um contrato.
   * @param contrato contrato para ser atualizado.
   */
  atualizarContrato(contrato: Contrato): Observable<Contrato> {
    return this.http.put<Contrato>(`${ApiEndpointBase}/contrato/${contrato.id}`, contrato);
  }

  // #endregion

  // #region Certificado Digital

  /**
   * Retorna recursos assinaturas NÃO VENCIDOS.
   */
  obterRecursosAssinaturas(recursoUsadoNoMomento?: number): Observable<RecursoAssinatura[]> {
    let url = `${ApiEndpointBase}/recursoassinatura`;
    if (recursoUsadoNoMomento != undefined && recursoUsadoNoMomento > 0)
      url += `?incluir=${recursoUsadoNoMomento}`;

    return this.http.get<RecursoAssinatura[]>(url);
  }

  /**
   * Retorna true se há recursos assinaturas NÃO VENCIDOS.
   */
  obterSeHaRecursosAssinaturas(): Observable<boolean> {
    return this.http.get<boolean>(`${ApiEndpointBase}/recursoassinatura/tem-recurso`);
  }

  obterCertificados(filtro: FiltroCertificadoDigital): Observable<CertificadoDigitalCollection> {

    const httpParams = this.converterFiltroParaQueryParam(filtro);

    return this.http.get<CertificadoDigitalCollection>(`${ApiEndpointBase}/certificado-digital`, { params: httpParams });
  }

  obterProcuracoes(certificadoID: number, pageIndex = 1, pesquisa: string = ""): Observable<Paginador<Procuracao>> {
    return this.http.get<Paginador<Procuracao>>(`${ApiEndpointBase}/procuracao?certificadoID=${certificadoID}&pageIndex=${pageIndex}&pesquisa=${pesquisa}`);
  }

  obterProcuracoesComAdvertencias(certificadoID: number): Observable<ProcuracaoComAdvertencia[]> {
    return this.http.get<ProcuracaoComAdvertencia[]>(`${ApiEndpointBase}/procuracao/procuracao-com-advertencia?certificadoID=${certificadoID}`);
  }

  obterProcuracoesEmProcessamento(certificadoID: number): Observable<ProcuracaoEmProcessamento[]> {
    return this.http.get<ProcuracaoEmProcessamento[]>(`${ApiEndpointBase}/procuracao/procuracao-em-processamento?certificadoID=${certificadoID}`);
  }

  apagarProcuracoesComAdvertencias(certificadoID: number): Observable<void> {
    return this.http.delete<void>(`${ApiEndpointBase}/procuracao/procuracao-com-advertencia?certificadoID=${certificadoID}`);
  }

  apagarCertificado(certificadoId: number): Observable<any> {
    return this.http.delete(`${ApiEndpointBase}/certificado-digital/${certificadoId}`);
  }

  desconsiderarCertificado(certificadoId: number): Observable<any> {
    return this.http.delete(`${ApiEndpointBase}/certificado-digital/${certificadoId}/desconsiderar`);
  }

  downloadRelatorioCertificadosProcuracoes(): Observable<DownloadObject> {
    return this.http.post<DownloadObject>(`${ApiEndpointBase}/certificado-digital/download-relatorio`, null);
  }

  downloadRelatorioProcessosParalisados(filtro: FiltroProcessoParalisado): Observable<DownloadObject> {
    return this.http.post<DownloadObject>(`${ApiEndpointBase}/dashboard/download-relatorio-processos-paralisados`, filtro);
  }

  downloadRelatorioCertificadoProcuracaoEmVencimento(tipoVencimento: string): Observable<DownloadObject> {
    return this.http.post<DownloadObject>(`${ApiEndpointBase}/dashboard/download-relatorio-certificado-procuracao-vencimento/${tipoVencimento}`, null);
  }

  salvarNaoHomologado(id: number) {
    return this.http
      .post(
        `${ApiEndpointBase}/certificado-digital/salvar-nao-homologado`, id);
  }

  adicionarProcuracaoManualmente(procuracoes: AdicionarProcuracaoManualmente[], certificadoID: number) {
    return this.http.post(`${ApiEndpointBase}/certificado-digital/${certificadoID}/procuracoes/adicionar-procuracao-manualmente`, procuracoes)
  }

  // #endregion

  // #region Auditoria
  /**
   * Dado o token logado, retorna as infos a respeito do auditor utilizado. (Se houver)
   */
  obterDadosAuditoria(): Observable<DadosAuditor> {
    return this.http.get<DadosAuditor>(`${ApiEndpointBase}/conta/auditor`);
  }

  /**
   * Obtém resultado de auditoria para a competência e contratante especificados
   */
  obterResultadoAuditoria(filtro: FiltroDashboard): Observable<GestaoDeRiscos> {
    return this.http.post<GestaoDeRiscos>(`${ApiEndpointBase}/dashboard/auditoria/resultado`, filtro);
  }

  /**
   * Método usado para requisitar editar login do auditor
   * @param loginAuditor contém as informações do login do auditor
   */
  criarEditarAuditor(loginAuditor: LoginAuditor): Observable<RetornoLoginAuditor> {
    return zip(
      this.http.post<RetornoLoginAuditor>(`${ApiEndpointBase}/conta/auditor`, loginAuditor),
      this.obterDadosContratante(true)
    ).pipe(map(data => data[0]));
  }

  statusLoginAuditor(): Observable<StatusLoginAuditor> {
    return this.http.get<StatusLoginAuditor>(`${ApiEndpointBase}/conta/auditor/status-login`);
  }

/**
   * Download da Planilha Modelo das Procurações
   */
baixaPlanilhaModeloProcuracoes(): Observable<{ linkDownload: string }> {
  return this.http.get<{ linkDownload: string}>(`${ApiEndpointBase}/procuracao/download-planilha-modelo-procuracoes`);
}
  // #endregion

  // #region Domínio do sistema

  obterRangeNumeroClientes(): Observable<SelectModel<string, string>[]> {
    return this.http.get<SelectModel<string, string>[]>(`${ApiEndpointBase}/dominio/range/numero-clientes`)
      .pipe(share());
  }

  obterRangeNumeroFuncionarios(): Observable<SelectModel<string, string>[]> {
    return this.http.get<SelectModel<string, string>[]>(`${ApiEndpointBase}/dominio/range/numero-funcionarios`)
      .pipe(share());
  }

  obterLinkManualUploader(): Observable<string> {
    return this.http.get<string>(`${ApiEndpointBase}/dominios/link-manual-uploader`);
  }

  obterLinkPdfFaq(): Observable<string> {
    return this.http.get<string>(`${ApiEndpointBase}/dominios/link-pdf-faq`);
  }

  obterCanaisDeVenda(): Observable<SelectModel<number, string>[]> {
    return this.http.get<SelectModel<number, string>[]>(`${ApiEndpointBase}/dominios/canais-de-venda`)
      .pipe(share());
  }

  obterProdutos(): Observable<SelectModel<number, string>[]> {
    return this.http
      .get<SelectModel<number, string>[]>(
        `${ApiEndpointBase}/dominios/produtos`
      )
      .pipe(share());
  }

  obterParcerias(): Observable<SelectModel<number, string>[]> {
    return this.http.get<SelectModel<number, string>[]>(`${ApiEndpointBase}/dominios/parcerias`)
      .pipe(share());
  }

  obterSistemasContabeis(): Observable<SelectModel<number, string>[]> {
    return this.http.get<SelectModel<number, string>[]>(`${ApiEndpointBase}/dominios/sistemas-contabeis`)
      .pipe(share());
  }

  // #endregion

  // #region Cadastro do Contador

  obterPerfis(): Observable<SelectModel<number, string>[]> {
    return this.http.get<SelectModel<number, string>[]>(`${ApiEndpointBase}/dominios/perfis`)
      .pipe(share());
  }

  obterPerfisAdminProduto(): Observable<SelectModel<number, string>[]> {
    return this.http.get<SelectModel<number, string>[]>(`${ApiEndpointBase}/dominios/perfis-admin`)
      .pipe(share());
  }

  obterUsuarios(): Observable<UserListElement[]> {
    return this.http.get<UserListElement[]>(`${ApiEndpointBase}/usuario`);
  }

  obterUsuariosFiltro(): Observable<UsuarioCadastro[]> {
    return this.http.get<UsuarioCadastro[]>(`${ApiEndpointBase}/dashboard/usuario`);
  }

  obterUsuariosPorPagina(pageIndex: number): Observable<Paginador<UserListElement>> {
    return this.http.get<Paginador<UserListElement>>(`${ApiEndpointBase}/usuario/paginado?pageIndex=${pageIndex}`);
  }

  // TODO: Ver se precisa do Map ou se a api agora retorna ID
  salvarUsuario(user: UsuarioCadastro): Observable<UserListElement> {
    return this.http.post<any>(`${ApiEndpointBase}/usuario`, user)
      .pipe(map(userId => {
        return ({
          id: userId,
          nome: user.nome,
          email: user.email,
          perfilId: user.perfilId,
          telefone: user.telefone,
          notificacaoEmail: user.notificacaoEmail,
          notificacaoSms: user.notificacaoSms,
          podeRemover: true,
          situacao: user.situacao
        } as UserListElement);
      }));
  }

  removerUsuario(user: UserListElement): Observable<UserListElement> {
    if (!user.podeRemover) {
      return throwError({
        titulo: 'Impossível Remover',
        descricao: 'O usuário não pode ser removido',
      } as AppResponseError);
    }

    return this.http.delete<UserListElement>(`${ApiEndpointBase}/usuario/${user.id}`);
  }

  resendEmailChange(userId: number): Observable<UserListElement> {
    return this.http.get<UserListElement>(`${ApiEndpointBase}/usuario/${userId}/reenviar`);
  }

  /**
   * Atualiza usuário (muitas vezes, o contador)
   * @param user target a ser atualizado.
   */
  atualizarUsuario(user: UsuarioCadastro): Observable<UserListElement> {
    return this.http.put<UserListElement>(`${ApiEndpointBase}/usuario/${user.id}`, user);
  }


  ativarInativarUsuario(userId: number, ativar: boolean): Observable<any> {
    const action = ativar ? 'ativar' : 'inativar';
    return this.http.put(`${ApiEndpointBase}/usuario/${userId}/${action}`, undefined);
  }

  //#endregion

  // #region Clientes e Obrigações
  obterRamosAtividade(): Observable<SelectModel<number, string>[]> {
    return this.http.get<SelectModel<number, string>[]>(`${ApiEndpointBase}/dominios/ramodeatividade`)
      .pipe(share());
  }

  obterPrazosIcmsIpi(): Observable<ObrigacaoPrazo[]> {
    return this.http.get<ObrigacaoPrazo[]>(`${ApiEndpointBase}/dominios/prazos/icms-ipi`)
      .pipe(share());
  }

  obterPrazoEfdContribuicoes(): Observable<number> {
    /**
     * Exemplo de response
     * {
     *  "ID": "PrazoEfdContribuicoes",
     *  "Nome": 10
     * }
     * Por isso o first, e map
     */
    return this.http.get<SelectModel<number, string>>(`${ApiEndpointBase}/dominios/prazos/efd-contribuicoes`)
      .pipe(
        first(),
        map(v => +v.nome),
        share()
      );
  }

  obterClientesPaginados(flt?: QueryStringParams): Observable<Paginador<Cliente>> {
    const queryParam = this.buildQueryObject(flt);
    return this.http.get<Paginador<Cliente>>(`${ApiEndpointBase}/cliente/paginado`, {
      params: queryParam
    }).pipe(map((pagina: Paginador<Cliente>) => {
      pagina.conteudo.map((cliente: Cliente) => {
        // temp é o número registro com máscara
        let temp = cliente.cnpjCpf;
        if (temp.length === 11) {
          temp = `${temp.substring(0, 3)}.${temp.substring(3, 6)}.${temp.substring(6, 9)}-${temp.substring(9)}`;
        } else if (temp.length === 14) {
          temp = `${temp.substring(0, 2)}.${temp.substring(2, 5)}.${temp.substring(5, 8)}/${temp.substring(8, 12)}-${temp.substring(12)}`;
        }

        cliente.cnpjCpfNome = `${temp}`;
        if (cliente.scp && cliente.scp.length > 0) {
          cliente.cnpjCpfNome += ` - SCP: ${cliente.scp}`;
        }
        if (cliente.inscricaoEstadual && cliente.inscricaoEstadual.length > 0) {
          cliente.cnpjCpfNome += ` - IE: ${cliente.inscricaoEstadual}`;
        }
        cliente.cnpjCpfNome += ` - ${cliente.nome}`;

        return cliente;
      });
      return pagina;
    }));
  }

  obterClientesPaginadosJustificativaEmLote(filtro: FiltroJustificativaEmLote): Observable<PaginadorSelecao<ClienteJustificativaEmLote>> {
    return this.http.post<PaginadorSelecao<ClienteJustificativaEmLote>>(`${ApiEndpointBase}/cliente/paginado/justificativa-em-lote`, filtro).pipe(map((pagina: PaginadorSelecao<ClienteJustificativaEmLote>) => {
      pagina.conteudo.map((objeto: ClienteJustificativaEmLote) => {
        // temp é o número registro com máscara
        let temp = objeto.empresaCliente.cnpjCpf;
        if (temp.length === 11) {
          temp = `${temp.substring(0, 3)}.${temp.substring(3, 6)}.${temp.substring(6, 9)}-${temp.substring(9)}`;
        } else if (temp.length === 14) {
          temp = `${temp.substring(0, 2)}.${temp.substring(2, 5)}.${temp.substring(5, 8)}/${temp.substring(8, 12)}-${temp.substring(12)}`;
        }

        objeto.empresaCliente.cnpjCpfNome = `${temp}`;
        if (objeto.empresaCliente.scp && objeto.empresaCliente.scp.length > 0) {
            objeto.empresaCliente.cnpjCpfNome += ` - SCP: ${objeto.empresaCliente.scp}`;
        }
        if (objeto.empresaCliente.inscricaoEstadual && objeto.empresaCliente.inscricaoEstadual.length > 0) {
            objeto.empresaCliente.cnpjCpfNome += ` - IE: ${objeto.empresaCliente.inscricaoEstadual}`;
        }
        objeto.empresaCliente.cnpjCpfNome += ` - ${objeto.empresaCliente.nome}`;

        return objeto;
      });
      return pagina;
    }));
  }

  obterClientes(flt?: QueryStringParams): Observable<Cliente[]> {
    const queryParam = this.buildQueryObject(flt);
    return this.http.get<Cliente[]>(`${ApiEndpointBase}/cliente`, {
      params: queryParam
    }).pipe(map((clientes: Cliente[]) =>
      clientes.map((cliente: Cliente) => {
        // temp é o número registro com máscara
        let temp = cliente.cnpjCpf;
        if (temp.length === 11) {
          temp = `${temp.substring(0, 3)}.${temp.substring(3, 6)}.${temp.substring(6, 9)}-${temp.substring(9)}`;
        } else if (temp.length === 14) {
          temp = `${temp.substring(0, 2)}.${temp.substring(2, 5)}.${temp.substring(5, 8)}/${temp.substring(8, 12)}-${temp.substring(12)}`;
        }

        cliente.cnpjCpfNome = `${temp}`;
        if (cliente.scp && cliente.scp.length > 0) {
          cliente.cnpjCpfNome += ` - SCP: ${cliente.scp}`;
        }
        if (cliente.inscricaoEstadual && cliente.inscricaoEstadual.length > 0) {
          cliente.cnpjCpfNome += ` - IE: ${cliente.inscricaoEstadual}`;
        }
        cliente.cnpjCpfNome += ` - ${cliente.nome}`;

        return cliente;
      }).sort((a, b) => a.cnpjCpfNome.localeCompare(b.cnpjCpfNome))
    ));
  }

  obterClientesComObrigacao(flt?: QueryStringParams): Observable<Cliente[]> {
    const queryParam = this.buildQueryObject(flt);
    return this.http.get<Cliente[]>(`${ApiEndpointBase}/cliente/com-obrigacao`, {
      params: queryParam
    }).pipe(map((clientes: Cliente[]) =>
      clientes.map((cliente: Cliente) => {
        // temp é o número registro com máscara
        let temp = cliente.cnpjCpf;
        if (temp.length === 11) {
          temp = `${temp.substring(0, 3)}.${temp.substring(3, 6)}.${temp.substring(6, 9)}-${temp.substring(9)}`;
        } else if (temp.length === 14) {
          temp = `${temp.substring(0, 2)}.${temp.substring(2, 5)}.${temp.substring(5, 8)}/${temp.substring(8, 12)}-${temp.substring(12)}`;
        }

        cliente.cnpjCpfNome = `${temp}`;
        if (cliente.scp && cliente.scp.length > 0) {
          cliente.cnpjCpfNome += ` - SCP: ${cliente.scp}`;
        }
        if (cliente.inscricaoEstadual && cliente.inscricaoEstadual.length > 0) {
          cliente.cnpjCpfNome += ` - IE: ${cliente.inscricaoEstadual}`;
        }
        cliente.cnpjCpfNome += ` - ${cliente.nome}`;

        return cliente;
      }).sort((a, b) => a.cnpjCpfNome.localeCompare(b.cnpjCpfNome))
    ));
  }

  obterClientesPorContratante(empresaContratanteId: number): Observable<Cliente[]> {
    return this.http.get<Cliente[]>(`${ApiEndpointBase}/cliente/por-contratante/${empresaContratanteId}`).pipe(map((clientes: Cliente[]) =>
      clientes.map((cliente: Cliente) => {
        // temp é o número registro com máscara
        let temp = cliente.cnpjCpf;
        if (temp.length === 11) {
          temp = `${temp.substring(0, 3)}.${temp.substring(3, 6)}.${temp.substring(6, 9)}-${temp.substring(9)}`;
        } else if (temp.length === 14) {
          temp = `${temp.substring(0, 2)}.${temp.substring(2, 5)}.${temp.substring(5, 8)}/${temp.substring(8, 12)}-${temp.substring(12)}`;
        }

        cliente.cnpjCpfNome = `${temp}`;
        if (cliente.scp && cliente.scp.length > 0) {
          cliente.cnpjCpfNome += ` - SCP: ${cliente.scp}`;
        }
        if (cliente.inscricaoEstadual && cliente.inscricaoEstadual.length > 0) {
          cliente.cnpjCpfNome += ` - IE: ${cliente.inscricaoEstadual}`;
        }
        cliente.cnpjCpfNome += ` - ${cliente.nome}`;

        return cliente;
      })
    ));
  }

  obterParametrosDeNotificacao(clienteId: number = null): Observable<ParametrosAutomacao> {
    return this.http.get<ParametrosAutomacao>(`${ApiEndpointBase}/cliente/${clienteId}/parametros-automacao`);
  }

  obterContatosCliente(clientId: number): Observable<Contato[]> {
    if (clientId === null) {
      return of([]);
    }
    return this.http.get<Contato[]>(`${ApiEndpointBase}/cliente/${clientId}/contato`);
  }

  obterResponsaveisCliente(clientId?: number): Observable<Responsavel[]> {
    if (clientId === null) {
      return of([]);
    }

    return this.http.get<Responsavel[]>(`${ApiEndpointBase}/cliente/${clientId}/responsavel`);
  }

  obterResponsaveis(): Observable<Responsavel[]> {
    return this.http.get<Responsavel[]>(`${ApiEndpointBase}/cliente/responsavel`);
  }

  obterObrigacoesCliente(clientId: number): Observable<Obrigacao[]> {
    if (clientId === null) {
      return of([]);
    }
    return this.http.get<Obrigacao[]>(`${ApiEndpointBase}/cliente/${clientId}/obrigacao`);
  }

  obterObrigacoes(): Observable<SelectableModel<number, string>[]> {
    return this.http.get<SelectableModel<number, string>[]>(`${ApiEndpointBase}/obrigacao`)
      .pipe(share());
  }

  obterCliente(id: number): Observable<Cliente> {
    return this.http.get<Cliente>(`${ApiEndpointBase}/cliente/${id}`);
  }

  /**
   * Ativa ou desativa uma Empresa Cliente
   * @param client Cliente a ser alterado
   * @returns o cliente recém alterado
   */
  ativarInativarCliente(empresaClientID: number, valorAtivacao: boolean, retomarLicencas: boolean): Observable<Cliente> {
    return this.http.patch<Cliente>(`${ApiEndpointBase}/cliente/ativar-inativar`, {
      empresaClienteID: empresaClientID,
      valorAtivacao,
      retomarLicencas
    });
  }

  /**
   * Verifica se a Empresa Cliente cumpriu carência das Licenças.
   * @param empresaClienteId Empresa Cliente a ser consultada
   * @returns relação de licenças e se cumpriu carência.
   */
  clienteCumpriuCarencia(empresaClienteId: number): Observable<ClienteCumpriuCarenciaEmLicenca> {
    return this.http.get<ClienteCumpriuCarenciaEmLicenca>(`${ApiEndpointBase}/cliente/${empresaClienteId}/carencia`);
  }

  /**
   * Cria uma nova Empresa Cliente e já faz todas as requisições para completar o cadastro
   * @param client Cliente a ser criado
   * @returns o cliente recém salvo
   */
  salvarClienteCompleto(client: Cliente): Observable<Cliente> {
    return this.http.post<Cliente>(`${ApiEndpointBase}/cliente/completo`, client);
  }


  /**
   * Cria uma nova Empresa Cliente e já faz todas as requisições para completar o cadastro
   * @param client Cliente a ser criado
   * @returns o cliente recém salvo
   */
  atualizarClienteCompleto(client: Cliente): Observable<Cliente> {
    return this.http.put<Cliente>(`${ApiEndpointBase}/cliente/${client.id}/completo`, client);
  }

  /**
   * Tenta apagar um cliente do banco, deve falhar caso o cliente ainda esteja em uso.
   * @param id ID do cliente a ser excluído.
   */
  removerCliente(id: number): Observable<any> {
    return this.http.delete(`${ApiEndpointBase}/cliente/${id}`);
  }


  /**
   * Associa um responsavel a uma empresa cliente.
   * @param clientId Número Identificador da Empresa Cliente
   * @param responsavelId Número Identificador do Responsável (Usuário)
   * @param obrigacaoId Número de Identificação da Obrigação à associar.
   */
  associarResponsavel(clientId: number, responsavelId: number, obrigacaoId): Observable<any> {
    return this.http
      .post<Cliente>(`${ApiEndpointBase}/cliente/${clientId}/responsavel/${responsavelId}/obrigacao/${obrigacaoId}`,
        undefined);
  }


  atualizarNotificacoesDosContatos(clientId: number, opcoesDeNotificacao: OpcoesNotificacaoContatoCliente): Observable<any> {
    return this.http.patch(`${ApiEndpointBase}/cliente/${clientId}/contato/notificacoes`, opcoesDeNotificacao);
  }


  /**
   * Recria a lista de responsáveis, de acordo com a lista informada
   * @param clientId Número Identificador da Empresa Cliente
   * @param responsaveis Coleção de responsáveis à atribuir para Empresa Cliente.
   */
  atualizarColecaoDeResponsaveis(clientId: number, responsaveis: ResponsavelCadastro[]): Observable<any> {
    return this.http.put(`${ApiEndpointBase}/cliente/${clientId}/responsavel`, responsaveis);
  }

  /**
   * Atualiza os parametros de automação de um cliente. A omissão de campos não anula eles.
   * @param clientId ID Do cliente para atualizar
   * @param parametros Parametros da automação para atualizar
   * @param completarCadastro Se for true, após editar os campos, altera o valor da coluna TerminouCadastro na Emp. Cli.
   */
  atualizarParametrosAutomacao(
    clientId: number,
    parametros: ParametrosAutomacao,
    completarCadastro: boolean = false): Observable<any> {
    return this.http
      .patch(`${ApiEndpointBase}/cliente/${clientId}/parametros-automacao?completar-cadastro=${completarCadastro}`, parametros);
  }


  /**
   * Apaga um responsável e todas suas obrigações associadas
   * @param clientId Número Identificador da Empresa Cliente
   * @param responsavelId Número Identificador do Responsável (Usuário)
   */
  excluirResponsavel(clientId: number, responsavelId: number): Observable<any> {
    return this.http.post<Cliente>(`${ApiEndpointBase}/cliente/${clientId}/responsavel/${responsavelId}?apagarTodasObrigacoes=true`, []);
  }

  /**
   * Apaga apenas alguma(s) obrigacao(oes) de um responsável
   * @param clientId Número Identificador da Empresa Cliente
   * @param responsavelId Número Identificador do Responsável (Usuário)
   * @param obrigacoes Colecao de obrigacoes para remover
   */
  desassociarResponsavelObrigacao(clientId: number, responsavelId: number, obrigacoes: SelectModel<number, string>[]): Observable<any> {
    return this.http.post<Cliente>(`${ApiEndpointBase}/cliente/${clientId}/responsavel/${responsavelId}`, obrigacoes);
  }



  /**
   * Salva uma nova categoria para a EmpresaContratante (Não é associarCategoriaCliente)
   * @param category Nova categoria para adicionar a EmpresaContratante
   */
  salvarCategoria(category: GroupSelectModel<number, string>): Observable<GroupSelectModel<number, string>> {
    return this.http.post<GroupSelectModel<number, string>>(`${ApiEndpointBase}/categoria`, category);
  }

  /**
   * Remove uma categoria da Empresa Contratante
   * @param categoryId ID da categoria a ser removida.
   */
  removerCategoria(categoryId: number): Observable<any> {
    return this.http.delete<any>(`${ApiEndpointBase}/categoria/${categoryId}`);
  }

  /**
   * Dado um cliente, apaga todas suas categorias e seta a coleção de categorias fornecida
   * @param clientId Cliente a atualizar
   * @param categories Categorias a atrelar
   */
  atualizarCategoriasCliente(clientId: number, categories: GroupSelectModel<number, string>[]): Observable<any> {
    return this.http.put<GroupSelectModel<number, string>>
      (`${ApiEndpointBase}/cliente/${clientId}/categoria`, categories);
  }

  /**
   * Salva um contato no banco de dados
   * @param clientId Id do cliente a receber o contato
   * @param contato Contato a ser salvo
   */
  salvarContato(clientId: number, contato: Contato): Observable<Contato> {
    return this.http.post<Contato>(`${ApiEndpointBase}/cliente/${clientId}/contato`, contato);
  }


  /**
   * Apaga todos os contatos do cliente e salva os informados
   * @param clientId Id do cliente a salvar os contatos
   * @param contatos Coleção de contatos a ser salvo
   */
  atualizarContatos(clientId: number, contatos: Contato[]): Observable<any> {
    return this.http.put<any>(`${ApiEndpointBase}/cliente/${clientId}/contato`, contatos);
  }

  obterUfs(): Observable<SelectModel<string, string>[]> {
    return this.http.get<SelectModel<string, string>[]>(`${ApiEndpointBase}/dominios/ufs`);
  }

  obterNotificacoesContatoCliente(clientId: number = null): Observable<OpcoesNotificacaoContatoCliente> {
    return this.http
      .get<OpcoesNotificacaoContatoCliente>(`${ApiEndpointBase}/cliente/${clientId}/contato/notificacoes`);
  }

  obterCnaes(): Observable<Cnae[]> {
    return this.http.get<Cnae[]>(`${ApiEndpointBase}/dominios/cnae`);
  }

  obterCategoriasCliente(clientId: number = null): Observable<GroupSelectModel<number, string>[]> {
    if (clientId === null) {
      return of([]);
    }

    return this.http.get<any[]>(`${ApiEndpointBase}/cliente/${clientId}/categoria`)
      .pipe(
        map(v => v.map(x => ({ id: x.id, nome: x.nome, grupo: x.grupo }) as GroupSelectModel<number, string>)
        ));

  }

  obterCategorias(filtro?: FiltroCategorias): Observable<GroupSelectModel<number, string>[]> {

    const queryParam = this.converterFiltroParaQueryParam(filtro);

    return this.http.get<GroupSelectModel<number, string>[]>(`${ApiEndpointBase}/categoria`, {
      params: queryParam
    });
  }

  /**
   * importa o conjunto de responsaveis de um cliente para o outro
   * @param clienteDestinoId Cliente de Destino ID
   * @param clienteOrigemId Cliente de origem ID
   */
  importarResponsaveisCliente(clienteDestinoId: number = null, clienteOrigemId: number = null): Observable<Responsavel[]> {
    return this.http
      .post<Responsavel[]>(`${ApiEndpointBase}/cliente/${clienteDestinoId}/responsavel/importar/${clienteOrigemId}`, undefined);
  }

  /**
   * Importa a coleção de contatos de um cliente para outro.
   * @param clienteDestinoId Cliente de Destino ID
   * @param clienteOrigemId Cliente de origem ID
   */
  importarContatosCliente(clienteDestinoId: number = null, clienteOrigemId: number = null): Observable<Contato[]> {
    return this.http.post<Contato[]>(`${ApiEndpointBase}/cliente/${clienteDestinoId}/contato/importar/${clienteOrigemId}`, undefined);
  }

  /**
   * Importa as opções de notificação de contatos de um cliente para outro.
   * @param clienteDestinoId Cliente de Destino ID
   * @param clienteOrigemId Cliente de Origem ID
   */
  importarOpcoesNotificacaoContatosCliente(clienteDestinoId: number = null, clienteOrigemId: number = null): Observable<Contato[]> {
    return this.http
      .post<Contato[]>(`${ApiEndpointBase}/cliente/${clienteDestinoId}/contato/notificacoes/importar/${clienteOrigemId}`, undefined);
  }

  /**
   *  Importa os riscos aceitáveis dos parametros de automação de outra empresa cliente
   * @param clienteDestinoId Cliente de Destino ID
   * @param clienteOrigemId Cliente de origem ID
   */
  importarParametrosAutomacaoRiscoAceitavel(
    clienteDestinoId: number = null,
    clienteOrigemId: number = null): Observable<ParametrosAutomacao> {
    return this.http.post<ParametrosAutomacao>
      (`${ApiEndpointBase}/cliente/${clienteDestinoId}/parametros-automacao/risco-aceitavel/importar/${clienteOrigemId}`, undefined);
  }

  /**
   *  Importa a gestão de prazos dos parâmetros de automação de uma outra empresa cliente.
   * @param clienteDestinoId Cliente de Destino ID
   * @param clienteOrigemId Cliente de origem ID
   */
  importarParametrosAutomacaoGestaoDePrazos(
    clienteDestinoId: number = null,
    clienteOrigemId: number = null): Observable<ParametrosAutomacao> {
    return this.http.post<ParametrosAutomacao>
      (`${ApiEndpointBase}/cliente/${clienteDestinoId}/parametros-automacao/gestao-de-prazos/importar/${clienteOrigemId}`, undefined);
  }

  /**
   *  Exporta uma coleção de responsaveis de um cliente para N outros
   * @param clienteOrigemID ID do cliente a usar a lista
   * @param clientesDestino Coleção de empresas destino a receber os responsaveis.
   */
  exportarResponsaveis(clienteOrigemID: number, clientesDestino: SelectModel<number, string>[]): Observable<any> {
    return this.http.post(`${ApiEndpointBase}/cliente/${clienteOrigemID}/responsavel/exportar`, clientesDestino);
  }

  /**
   * Método auto-explicativo.
   * @param clienteOrigemID ID do cliente a usar os prazos
   * @param clientesDestino Coleção de empresas destino a receber
   */
  exportarParametrosAutomacaoGestaoDePrazos(clienteOrigemID: number, clientesDestino: SelectModel<number, string>[]): Observable<any> {
    return this.http.post(`${ApiEndpointBase}/cliente/${clienteOrigemID}/parametros-automacao/gestao-de-prazos/exportar`, clientesDestino);
  }

  /**
   * Método auto-explicativo.
   * @param clienteOrigemID ID do cliente a usar os riscos
   * @param clientesDestino Coleção de empresas destino a receber
   * @returns Observable com o result da API.
   */
  exportarParametrosAutomacaoRiscoAceitavel(clienteOrigemID: number, clientesDestino: SelectModel<number, string>[]): Observable<any> {
    return this.http.post(`${ApiEndpointBase}/cliente/${clienteOrigemID}/parametros-automacao/risco-aceitavel/exportar`, clientesDestino);
  }


  /**
   * Método auto-explicativo.
   * @param clienteOrigemID ID do cliente a usar os riscos
   * @param clientesDestino Coleção de empresas destino a receber
   */
  exportarOpcoesDeNotificacaoContato(clienteOrigemID: number, clientesDestino: SelectModel<number, string>[]): Observable<any> {
    return this.http.post(`${ApiEndpointBase}/cliente/${clienteOrigemID}/contato/notificacoes/exportar`, clientesDestino);
  }

  /**
   * Método auto-explicativo.
   * @param clienteOrigemID ID do cliente a usar os riscos
   * @param clientesDestino Coleção de empresas destino a receber
   */
  exportarContatos(clienteOrigemID: number, clientesDestino: SelectModel<number, string>[]): Observable<any> {
    return this.http.post(`${ApiEndpointBase}/cliente/${clienteOrigemID}/contato/exportar`, clientesDestino);
  }

  /**
   * Dada uma categoria específica, atrela ela ao cliente.
   * @param clientID ID da EmpresaCliente
   * @param categoryId ID da categoria
   */
  associarCategoriaCliente(clientID: number, categoryId: number): Observable<any> {
    return this.http.post(`${ApiEndpointBase}/cliente/${clientID}/categoria/${categoryId}`, undefined);
  }

  /**
   * Dada uma categoria específica, desassocia ela ao cliente.
   * @param clientID ID da EmpresaCliente
   * @param categoryId ID da categoria
   */
  desassociarCategoriaCliente(clientID: number, categoryId: number): Observable<any> {
    return this.http.delete(`${ApiEndpointBase}/cliente/${clientID}/categoria/${categoryId}`);
  }

  /**
   * Apaga um contato de um cliente e remove todas as suas associações.
   * @param clientID ID da EmpresaCliente
   * @param contactId ID do Contato
   */
  removerContato(clientID: number, contactID: number): Observable<any> {
    return this.http.delete(`${ApiEndpointBase}/cliente/${clientID}/contato/${contactID}`);
  }

  uploadCertificado(file: CertificateDroppedFile): Observable<any> {
    const formData = new FormData();
    formData.append('arquivo', file.file, file.name);
    formData.append('senha', file.password);
    return this.http.post(`${ApiEndpointBase}/certificado-digital`, formData);
  }

atualizaCertificado(certificadoId: number, senha?: string): Observable<any> {
  return this.http.put(`${ApiEndpointBase}/certificado-digital/atualizar/${certificadoId}`, senha ? { senha } : {});
}

  buscarProcuracoesCertificado(certificadoId: number, listaCnpj: string[]): Observable<any> {
    return this.http.put<Observable<any>>(`${ApiEndpointBase}/certificado-digital/${certificadoId}`, listaCnpj, );
  }

  reiniciarBuscaProcuracoes(certificadoId: number): Observable<any> {
    return this.http.put<Observable<any>>(`${ApiEndpointBase}/certificado-digital/${certificadoId}/procuracoes/reiniciar-busca`, null);
  }

  homologarProcuracoesCertificado(certificadoId: number): Observable<any> {
    return this.http.put<Observable<any>>(`${ApiEndpointBase}/certificado-digital/${certificadoId}/procuracao/homologar`, null);
  }

  uploadSped(file: DroppedFile): Observable<Cliente[]> {
    return new Observable(obs => {
      const formData = new FormData();
      formData.append('arquivo', file.file, file.name);
      this.http.post(`${ApiEndpointBase}/cliente/sped`, formData, { observe: 'response' })
        .subscribe(
          (response) => {
            const client = response.body as Cliente[];
            obs.next(client);
          },
          (err: any) => obs.error(err)
        );
    });
  }


  obterParametrosAutomacao(clienteId: number = null): Observable<ParametrosAutomacao> {
    return this.http.get<ParametrosAutomacao>(`${ApiEndpointBase}/cliente/${clienteId}/parametros-automacao`)
      .pipe(map(v => v || {} as ParametrosAutomacao));
  }

  //#endregion

  // #region Empresa Contratante

  /**
   * Dado o token logado, retorna infos da Empresa Contratante
   */
  obterDadosContratante(forceNewRequest: boolean = false): Observable<EmpresaContratante> {
    if (!this.cachedRequests$.empresaContratante || forceNewRequest) {
      this.cachedRequests$.empresaContratante = this.http
        .get<EmpresaContratante>(`${ApiEndpointBase}/conta/contratante`)
        .pipe(shareReplay(1));
    }
    return this.cachedRequests$.empresaContratante;
  }

  /**
   * Método usado para retornar dados de uma específica Empresa Contratante (Admin-only)
   * @param empresaContratanteId Target
   */
  obterDadosEmpresaContratante(empresaContratanteId: number): Observable<EmpresaContratante> {
    return this.http.get<EmpresaContratante>(`${ApiEndpointBase}/contratante/${empresaContratanteId}`);
  }

  contratantePossuiEmpresaClienteConcluida(): Observable<boolean> {
    return this.http.get<boolean>(`${ApiEndpointBase}/contratante/pode-prosseguir`);
  }

  /**
   * Método usado para atualizar a EmpresaContratante no Setup (cadastro contador).
   * Ou na área do admin.
   * @param ec Empresa Contratante a ser atualizada.
   */
  atualizarEmpresaContratante(ec: EmpresaContratante): Observable<EmpresaContratante> {
    // TODO: devido a um 'cache' para evitar chamadas extras, e devido ao modo como foi implementado,
    // quando o dado da contratante muda, devemos fazer uma nova chamada 'choca'.
    // se der, melhorar isso um dia
    return zip(this.http
      .put<EmpresaContratante>(`${ApiEndpointBase}/conta/contratante/${ec.id}`, ec, { headers: httpOptions.headers }),
      this.obterDadosContratante(true))
      .pipe(map(data => data[0]));
  }

  atualizarEmpresaContratanteAdmin(ec: EmpresaContratante): Observable<EmpresaContratante> {
    return this.http.put<EmpresaContratante>(`${ApiEndpointBase}/contratante/${ec.id}`, ec, { headers: httpOptions.headers })
  }

  /**
   * Atualiza o situacaoProcessoCadastral e o passoProcessoCadastral.
   */
  alterarSetup(ec: EmpresaContratante): Observable<EmpresaContratante> {
    // TODO: devido a um 'cache' para evitar chamadas extras, e devido ao modo como foi implementado,
    // quando o dado da contratante, devemos fazer uma nova chamada 'choca'.
    // se der, melhorar isso um dia
    return zip(this.http.put<EmpresaContratante>(`${ApiEndpointBase}/contratante/alterar-processo-cadastral`, ec, { headers: httpOptions.headers }),
      this.obterDadosContratante(true))
      .pipe(map(data => data[0]));
  }

  /**
   * Atualiza o situacaoProcessoCadastral para 'Concluído' e envia emails para os usuários cadastrados.
   */
  terminarSetup(): Observable<void> {
    // TODO: devido a um 'cache' para evitar chamadas extras, e devido ao modo como foi implementado,
    // quando o dado da contratante, devemos fazer uma nova chamada 'choca'.
    // se der, melhorar isso um dia
    return zip(this.http.post<void>(`${ApiEndpointBase}/contratante/terminar-setup`, undefined),
      this.obterDadosContratante(true))
      .pipe(map(data => data[0]));
  }

  /**
   * Renvia o e-mail de welcome da empresa contratante
   * @param ec Empresa contratante na qual vai ser enviado o email
   */
  reenviarEmailWelcome(ec: EmpresaContratante): Observable<void> {
    return this.http.post<void>(`${ApiEndpointBase}/contratante/reenviar-email/${ec.id}`, {})
  }

  /**
   * Limpa os processamentos da empresa contratante. Ação permitida somente em ambiente de desenvolvimento ou homolog
   * @param ec Empresa contratante a ser feita a limpeza
   */
  limparProcessamentos(empresaID: number): Observable<void> {
    return this.http.delete<void>(`${ApiEndpointBase}/development/limpar-processamentos/${empresaID}`, {})
  }

  removerEmpresaContratante(empresaID: number): Observable<EmpresaContratante>{
    return this.http.delete<EmpresaContratante>(`${ApiEndpointBase}/contratante/${empresaID}`, {})
  }

  //#endregion

  // #region Usuário

  /**
   * Faz login pro usuário comum
   * @param user email
   * @param passwd senha
   */
  doLogin(user: string, passwd: string): Observable<TokenLogin> {
    return this.http.post<TokenLogin>(`${ApiEndpointBase}/conta/login`, {
      Email: user,
      Senha: passwd
    },
    //Necessário para utilizar cookies
    { withCredentials: true });
  }

  refreshToken(refr: RefreshRequest): Observable<TokenLogin> {
    return this.http.post<TokenLogin>(`${ApiEndpointBase}/conta/refresh`, refr);
  }

  /**
   * Inicia processo de recuperação de senha.
   * @param email email da conta esquecida
   */
  recuperarSenha(email: string): Observable<any> {
    return this.http.post<any>(`${ApiEndpointBase}/conta/recuperacao-senha?email=` + encodeURIComponent(email), undefined);
  }

  /**
   * Inicia processo para alteração de senha na área logada.
   * @param email
   * @returns
   */
  alterarSenha(email: string): Observable<any> {
    return this.http.post<any>(`${ApiEndpointBase}/conta/alteracao-senha?email=` + encodeURIComponent(email), undefined);
  }

  obterLicencasContratante(filtros: FiltroLicenca): Observable<QuantidadeDeLicencas> {
    const httpParams = this.converterFiltroParaQueryParam(filtros);

    return this.http
      .get<QuantidadeDeLicencas>(`${ApiEndpointBase}/contratante/licencas/quantidade`,
        { params: httpParams });
  }

  obterLicencasAuditorContratante(filtros: FiltroLicenca): Observable<QuantidadeDeLicencasAuditor> {
    const httpParams = this.converterFiltroParaQueryParam(filtros);

    return this.http
      .get<any>(`${ApiEndpointBase}/contratante/licencasAuditor/quantidade`, { params: httpParams })
      .pipe(map(raw => mapQuantidadeDeLicencasAuditor(raw)));
  }

  obterUsuarioPorEmail(email: string): Observable<UserListElement> {
    return this.http.get<UserListElement>(`${ApiEndpointBase}/usuario/filtrado?email=${encodeURIComponent(email)}`)
  }

  notificarMultifator(mfaNotificacao: MFANotificacao): Observable<any>{
    return this.http.post<any>(`${ApiEndpointBase}/conta/notificar-multifator`, mfaNotificacao,
    //Necessário para utilizar cookies
    { withCredentials: true });
  }

  verificarMultifator(mfaValidacao: MFAValidacao): Observable<any>{
    return this.http.post<any>(`${ApiEndpointBase}/conta/verificar-multifator`, mfaValidacao,
    //Necessário para utilizar cookies
    { withCredentials: true });
  }

  //#endregion

  // #region Uploader
  baixarUploader(): Observable<{ linkDownload: string }> {
    return this.http.get<{ linkDownload: string }>(`${ApiEndpointBase}/download/uploader`);
  }

  obterUltimaVersaoUploader(): Observable<{ versao: string }> {
    return this.http.get<{ versao: string }>(`${ApiEndpointBase}/dashboard/ultima-versao-uploader`);
  }
  // #endregion

  // #region Dashboard
  public obterDashboardPrincipal(filtro: FiltroDashboard): Observable<Principal> {
    return this.http.post<Principal>(`${ApiEndpointBase}/dashboard/principal`, filtro);
  }

  public obterCertificadosVencidos(): Observable<PrazoCertificadosProcuracoes> {
    return this.http.get<PrazoCertificadosProcuracoes>(`${ApiEndpointBase}/dashboard/certificados-vencidos`);
  }

  public obterProcessosParalisados(filtro: FiltroProcessoParalisado): Observable<ProcessosAguardandoAcao> {
    return this.http.post<ProcessosAguardandoAcao>(`${ApiEndpointBase}/dashboard/processos-paralisados`, filtro)
      .pipe(map((data: ProcessosAguardandoAcao) => {
        return data || ({
          processoTotalmenteInterrompido: [],
          processoParcialmenteInterrompido: [],
          cliente: {} as Cliente,
          competencia: 0,
          pagina: {
            numeroPagina: 1
          },
        } as ProcessosAguardandoAcao);
      }));
  }

  public existemProcessosParalisados(filtro: FiltroProcessoParalisado): Observable<boolean> {
    return this.http.post<boolean>(`${ApiEndpointBase}/dashboard/existem-processos-paralisados`, filtro);
  }

  public realizarAcaoUsuario(processamentoId: number, acao: string): Observable<void> {
    return this.http.put<void>(`${ApiEndpointBase}/dashboard/acao-usuario/${processamentoId}/${acao}`, null);
  }

  public realizarAcaoJustificativaEmLote(justificativa: JustificativaEmLote): Observable<void> {
    return this.http.put<void>(`${ApiEndpointBase}/dashboard/acao-usuario/justificativa-em-lote`, justificativa);
  }

  public obterAutomacoes(filtro: FiltroDashboard): Observable<Automacao> {
    return this.http.post<Automacao>(`${ApiEndpointBase}/dashboard/automacao`, filtro);
  }

  public obterQuantidadeGestaoPrazos(filtro: FiltroDashboard): Observable<number> {
    return this.http.post<number>(`${ApiEndpointBase}/dashboard/quantidade-gestao-prazos`, filtro);
  }

  public obterDiretoriosMonitorados(): Observable<RespostaDeProgramasAbertos> {
    return this.http.get<RespostaDeProgramasAbertos>(`${ApiEndpointBase}/dashboard/programas-abertos`);
  }

  public obterLinkDownloadArquivoProcessamentoResultadoEtapa(path: string): Observable<{ linkDownload: string }> {
    return this.http.get<{ linkDownload: string }>(`${ApiEndpointBase}/${path}`);
  }

  public obterLinkDownloadTodoProcessamentoDaEtapa(path: string): Observable<any> {
    // e não passar o objeto de options, da um erro e nao cai ali no map
    return this.http.get(`${ApiEndpointBase}/${path}`, {
      responseType: 'blob'
    }).pipe(map(response => {
      // TODO: aqui deveriamos conseguir o bytes retornados pelo servidor, mas não consegui
      return response;
    }));
  }

  public obterHistoricoEtapa(etapa: string, competencia: Date, clienteId: number, obrigacaoId: number): Observable<HistoricoEtapa> {
    return this.http.get<HistoricoEtapa>(
      `${ApiEndpointBase}/processamento/historico?etapa=${etapa}&competencia=${competencia}&obrigacao=${obrigacaoId}&cliente=${clienteId}`
    );
  }

  public obterRelatoriosValidacao(competencia: Date, clienteId: number, obrigacaoId: number): Observable<ExecucaoEtapa[]> {
    return this.http.get<ExecucaoEtapa[]>(
      `${ApiEndpointBase}/processamento/historico-relatorios?competencia=${competencia}&obrigacaoID=${obrigacaoId}&clienteID=${clienteId}`
    );
  }

  public obterHistoricoUsuario(empresaClienteID: number, obrigacaoID: number, competencia: Date,etapa: string): Observable<AcaoUsuarioProcessamento[]> {
    competencia = new Date(competencia);
    const competenciaInt = competencia.getFullYear() * 100 + competencia.getMonth() + 1;
    return this.http.get<AcaoUsuarioProcessamento[]>(
      `${ApiEndpointBase}/dashboard/acao-usuario/${etapa}/${empresaClienteID}/${obrigacaoID}/${competenciaInt}`
    );
  }


  public obterGraficos(filtro: FiltroDashboard, exibirDados: boolean): Observable<Graficos> {
    return this.http.post<Graficos>(`${ApiEndpointBase}/dashboard/graficos?exibirDados=${exibirDados}`, filtro);
  }

  public obterRelatorioCaixaSaida(messageID: number): Observable<({ template: string })> {
    return this.http.get<({ template: string })>(`${ApiEndpointBase}/central-de-notificacoes/saida/${messageID}/relatorio`);
  }

  public obterMenorCompetencia(isAtiva: boolean): Observable<Date> {
    return this.http.get<Date>(`${ApiEndpointBase}/cliente/menor-competencia?isAtiva=${isAtiva ?? ""}`);
  }

  public obterRelatorioArquivosTransmitidosPorPeriodo(filtro: FiltroRelatorio): Observable<DadosRelatorioArquivosTransmitidos[]> {
    const httpParams = this.converterFiltroParaQueryParam(filtro);

    return this.http.get<DadosRelatorioArquivosTransmitidos[]>(`${ApiEndpointBase}/admin-produto/relatorio-arquivos-transmitidos`, { params: httpParams });
  }

  public obterRelatorioConsolidado(filtro: FiltroRelatorio): Observable<DadosRelatorioConsolidado[]> {
    const httpParams = this.converterFiltroParaQueryParam(filtro);

    return this.http.get<DadosRelatorioConsolidado[]>(`${ApiEndpointBase}/admin-produto/relatorio-consolidado`, { params: httpParams });
  }

  public obterRelatorioAnalitico(dataInicial?: Date): Observable<DadosRelatorioAnalitico[]> {

    return this.http.get<DadosRelatorioAnalitico[]>(`${ApiEndpointBase}/admin-produto/relatorio-analitico?dataInicial=${dataInicial ?? ''}`);
  }

  public solicitarDownloadArquivosEmLote(filtro: FiltroDownloadLote): Observable<any> {
    return this.http.post<FiltroDownloadLote>(`${ApiEndpointBase}/dashboard/download-lote`, filtro);
  }

  // #endregion

  // #region métodos auxiliares
  /**
   * seta undefined para todos os obs com info salva na memória
   */
  zerarCacheDeRequests() {
    Object.keys(this.cachedRequests$)
      .forEach(key => {
        this.cachedRequests$[key] = undefined;
      });
  }

  getApiBaseUrl(): string {
    return ApiEndpointBase;
  }

  private converterFiltroParaQueryParam(filtro: any): HttpParams {

    if (!filtro) {
      return filtro;
    }

    let httpParams = new HttpParams();

    Object.keys(filtro).forEach(key => {
      if (filtro[key] instanceof Array) {
        filtro[key].forEach((item) => {
          httpParams = httpParams.append(`${key.toString()}`, item);
        });
      } else if (filtro[key] !== undefined && filtro[key] !== null) {
        httpParams = httpParams.append(key, filtro[key]);
      }
    });

    return httpParams;

  }

  private buildQueryObject(flt: QueryStringParams): {} {
    flt = flt || [];
    const queryParam = {};
    for (const f of flt) {
      if (f.fieldValue !== undefined) {
        queryParam[f.fieldName] = f.fieldValue;
      }
    }
    return queryParam;
  }
  // #endregion

  // #region ExcecaoPrazoObrigacao

  criarExcecaoObrigacaoPrazo(excecao: ExcecaoObrigacaoPrazo): Observable<ExcecaoObrigacaoPrazo> {
    return this.http.post<ExcecaoObrigacaoPrazo>(`${ApiEndpointBase}/excecao-obrigacao-prazo`, excecao);
  }

  obterExcecoesObrigacaoPrazoVigentes(): Observable<ExcecaoObrigacaoPrazo[]> {
    return this.http.get<ExcecaoObrigacaoPrazo[]>(`${ApiEndpointBase}/excecao-obrigacao-prazo/vigentes`);
  }

  obterExcecoesObrigacaoPrazo(): Observable<ExcecaoObrigacaoPrazo[]> {
    return this.http.get<ExcecaoObrigacaoPrazo[]>(`${ApiEndpointBase}/excecao-obrigacao-prazo`);
  }

  atualizarExcecaoObrigacaoPrazo(excecao: ExcecaoObrigacaoPrazo): Observable<ExcecaoObrigacaoPrazo> {
    return this.http.put<ExcecaoObrigacaoPrazo>(`${ApiEndpointBase}/excecao-obrigacao-prazo`, excecao);
  }
  //#endregion

  // #region Página de relatórios
  obterDadosRelatorioUtilizacaoProduto(filtro: FiltroRelatorioUtilizacaoProduto): Observable<DadosRelatorioUtilizacaoProduto> {
    return this.http.post<DadosRelatorioUtilizacaoProduto>(`${ApiEndpointBase}/relatorio/utilizacao-produto`, filtro);
  }

  downloadRelatorioUtilizacaoProduto(filtro: FiltroRelatorioUtilizacaoProduto): Observable<DownloadObject> {
    return this.http.post<DownloadObject>(`${ApiEndpointBase}/relatorio/download-relatorio-produto`, filtro);
  }

  obterDadosRelatorioEvolucaoProcessamento(filtro: FiltroRelatorioEvolucaoProcessamento, pagIndex: number, pageSize: number): Observable<Paginador<CardEvolucaoProcessamento>> {
    return this.http.post<Paginador<CardEvolucaoProcessamento>>(`${ApiEndpointBase}/relatorio/evolucao-processamento?pageIndex=${pagIndex}&pageSize=${pageSize}`, filtro);
  }

  downloadRelatorioEvolucaoProcessamento(filtro: FiltroRelatorioEvolucaoProcessamento): Observable<DownloadObject> {
    return this.http.post<DownloadObject>(`${ApiEndpointBase}/relatorio/download-evolucao-processamento`, filtro);
  }

  obterDadosRelatorioUtilizacaoLicencas(filtro: FiltroRelatorioLicencas, pagIndex: number, pageSize: number) : Observable<DadosRelatorioLicencas> {
    return this.http.post<DadosRelatorioLicencas>(`${ApiEndpointBase}/relatorio/utilizacao-licencas?pageIndex=${pagIndex}&pageSize=${pageSize}`, filtro);
  }

  downloadRelatorioUtilizacaoLicencas(filtro: FiltroRelatorioLicencas): Observable<DownloadObject> {
    return this.http.post<DownloadObject>(`${ApiEndpointBase}/relatorio/download-utilizacao-licencas`, filtro);
  }

  downloadRelatorioGestao(filtro: FiltroRelatorioGestao): Observable<DownloadObject> {
    return this.http.post<DownloadObject>(`${ApiEndpointBase}/relatorio/download-relatorio-gestao`, filtro);
  }

  // #endregion

  // #region Cadastro em lote

  obterCategoriasContratante(idContratante: number): Observable<GroupSelectModel<number, string>[]> {
    return this.http.get<GroupSelectModel<number, string>[]>(`${ApiEndpointBase}/cadastro-clientes-em-lote/categorias/${idContratante}`);
  }

  obterResponsaveisContratante(idContratante?: number): Observable<Responsavel[]> {
    return this.http.get<Responsavel[]>(`${ApiEndpointBase}/cadastro-clientes-em-lote/responsaveis/${idContratante}`);
  }

  salvarUsuarioEmLote(user: UsuarioCadastro[], idContratante?: number): Observable<any> {
    return this.http.post<any>(`${ApiEndpointBase}/cadastro-clientes-em-lote/responsaveis/${idContratante}`, user)
      .pipe(map(response => {
          var usuarios: UserListElement[] = response.usuariosCadastrados.map(u => {
            var infoUser = user.find(iu => iu.email === u.email)
            return {
                id: u.id,
                nome: infoUser.nome,
                email: infoUser.email,
                perfilId: infoUser.perfilId,
                telefone: infoUser.telefone,
                notificacaoEmail: infoUser.notificacaoEmail,
                notificacaoSms: infoUser.notificacaoSms,
                podeRemover: true,
                situacao: infoUser.situacao
              } as UserListElement
          })
        return {usuarios, erros: response.mensagensErros};
      }));
  }

  salvarCategoriasEmLote(categorias: GroupSelectModel<number, string>[], id?: number): Observable<GroupSelectModel<number, string>[]> {
      return this.http.post<GroupSelectModel<number, string>[]>(`${ApiEndpointBase}/cadastro-clientes-em-lote/categorias/${id}`, categorias);
  }

  baixarPlanilhaModelo(): Observable<{ linkDownload: string }> {
    return this.http.get<{ linkDownload: string }>(`${ApiEndpointBase}/cadastro-clientes-em-lote/download-planilha-modelo`);
  }

  cadastrarClientesEmLote(clientes: any[], idContratante: number): Observable<any> {
    return this.http.post<string[]>(`${ApiEndpointBase}/cadastro-clientes-em-lote/clientes/${idContratante}`, clientes.map(obj => obj.cliente))
        .pipe(map(response => {
            return { mensagens: response, linhas: clientes.map(obj => obj.linha) }
        }));
  }

  registrarProcessamentoCadastroClientesEmLote() : Observable<boolean> {
    return this.http.post(`${ApiEndpointBase}/cadastro-clientes-em-lote/registrar-processamento`,undefined).pipe(map(() => true));
  }

  atualizarProcessamentoCadastroClientesEmLote() : Observable<boolean> {
    return this.http.post(`${ApiEndpointBase}/cadastro-clientes-em-lote/atualizar-processamento`,undefined).pipe(map(() => true));
  }

  terminarProcessamentoCadastroClientesEmLote() : Observable<boolean> {
    return this.http.post(`${ApiEndpointBase}/cadastro-clientes-em-lote/terminar-processamento`,undefined).pipe(map(() => true));
  }

  atualizarInicioDasAutomacoes(id: number, inicioDasAutomacoes: EdicaoInicioDasAutomacoes){
    return this.http.put<GroupSelectModel<number, string>[]>(`${ApiEndpointBase}/admin-produto/editar-inicio-automacoes/${id}`, inicioDasAutomacoes);
  }

  /**
   * Retorna recursos assinaturas NÃO VENCIDOS de uma contratante especifica.
   */
  obterRecursosAssinaturaContratante(idContratante: number, recursoUsadoNoMomento?: number): Observable<RecursoAssinatura[]> {
    let url = `${ApiEndpointBase}/cadastro-clientes-em-lote/recursos-assinaturas/${idContratante}`;
    if (recursoUsadoNoMomento != undefined && recursoUsadoNoMomento > 0)
      url += `?incluir=${recursoUsadoNoMomento}`;
    return this.http.get<RecursoAssinatura[]>(url);
  }

  // #endregion

  // #region Manutenção em lote

  incluirObrigacaoLote(incluirObrigacao: IncluirObrigacao){
    return this.http.post(`${ApiEndpointBase}/manutencao-em-lote/incluir-obrigacao`, incluirObrigacao)
  }

  removerObrigacaoLote(removerObrigacao: RemoverObrigacao) {
    return this.http.post(`${ApiEndpointBase}/manutencao-em-lote/remover-obrigacao`, removerObrigacao)
  }

  incluirResponsaveis(incluirResponsaveis: IncluirRemoverResponsaveis){
    return this.http.post(`${ApiEndpointBase}/manutencao-em-lote/incluir-responsaveis`, incluirResponsaveis)
  }

  removerResponsaveis(removerResponsaveis: IncluirRemoverResponsaveis){
    return this.http.post(`${ApiEndpointBase}/manutencao-em-lote/remover-responsaveis`, removerResponsaveis)
  }

  salvarParametroAutomacao(parametroAutomacao: ParametroAutomacaoValues, acaoSelecionada: string) {
    const params = new HttpParams().set('acaoSelecionada', acaoSelecionada);
    return this.http.post(`${ApiEndpointBase}/manutencao-em-lote/parametro-automacao`, parametroAutomacao, { params });
  }
  salvarPrazoPorEtapa(prazoPorEtapa: PrazoPorEtapa){
    return this.http.post(`${ApiEndpointBase}/manutencao-em-lote/prazo-por-etapa`, prazoPorEtapa)
  }

  obterHistoricoAtivacaoEditor(contratanteID: number): Observable<Vigencia[]> {
    return this.http.get<Vigencia[]>(`${ApiEndpointBase}/contratante/${contratanteID}/editor/historico-ativacao`);
  }

  obterHistoricoAtivacaoAuditor(contratanteID: number): Observable<Vigencia[]> {
    return this.http.get<Vigencia[]>(`${ApiEndpointBase}/contratante/${contratanteID}/auditor/historico-ativacao`);
  }
  /**
   * Ativa e Inativa Clientes em Lote do Contratante, onde a interface ClientesSelecaoAtivaInativaCadastroEmLote é composta:
   * @param clientes[] coleção de clientes que serão alterados
   * @param licencasNaoUsadas quantidade de licenças disponíveis
   * @param modo qual será a alteração "ativar" | "inativar"
   * @return ClientesSelecaoAtivaInativaCadastroEmLote Objeto alterado e resposta do backend com a flag: alteradoComSucesso booleana
   * @author Elias A. Neri
   */
  ativarInativarEmpresaClientEmLote(dados: ClientesSelecaoAtivarInativarCadastroEmLote): Observable<ResultadoAtivarInativarClientesEmLote[]> {
    return this.http.put<ResultadoAtivarInativarClientesEmLote[]>(`${ApiEndpointBase}/manutencao-em-lote/ativar-inativar-empresas-clientes`, dados)
  }

  /**
   * Para cada empresa cliente informada, indica se há obrigações no histórico de alterações
   * (EFD Contribuições / EFD ICMS-IPI) elegíveis para reativação.
   * @param empresaClienteIds ids das empresas clientes selecionadas
   */
  obterObrigacoesHistoricoParaReativacao(empresaClienteIds: number[]): Observable<ObrigacoesHistoricoEmpresaCliente[]> {
    return this.http.post<ObrigacoesHistoricoEmpresaCliente[]>(`${ApiEndpointBase}/manutencao-em-lote/obrigacoes-historico-reativacao`, empresaClienteIds);
  }

  // #endregion
}
